/**
 * useNetworkState Hook
 * Provides centralized state management for the network editor
 */

import { useState, useCallback, useMemo } from 'react';
import { enableMapSet, produce } from 'immer';
import { useLocalStorage } from './useLocalStorage';
import { useHistory } from './useHistory';
import type { Node, Connection, NodeType, NetworkState, NodeProperties } from '@/types';
import { createNode } from '@/utils/network';
import { log, LOG_CATEGORIES } from '@/utils/logger';

enableMapSet();

// ============================================================
// TYPES
// ============================================================

/** Options for the useNetworkState hook */
export interface UseNetworkStateOptions {
  /** Initial nodes (overrides localStorage) */
  initialNodes?: Node[];
  /** Initial connections (overrides localStorage) */
  initialConnections?: Connection[];
  /** Whether to persist to localStorage */
  persist?: boolean;
  /** Storage key prefix */
  storageKey?: string;
}

/** Statistics about the network */
export interface NetworkStats {
  /** Total number of nodes */
  totalNodes: number;
  /** Count of each node type */
  nodesByType: Record<NodeType, number>;
  /** Total number of connections */
  totalConnections: number;
  /** Number of line connections */
  lines: number;
  /** Number of attachment connections */
  attachments: number;
  /** Whether network has any buses */
  hasBuses: boolean;
  /** Whether network is valid for power flow */
  isValidForPowerFlow: boolean;
}

/** Return type for the useNetworkState hook */
export interface UseNetworkStateReturn {
  // State
  /** Current nodes */
  nodes: Node[];
  /** Current connections */
  connections: Connection[];
  /** Currently selected node IDs */
  selectedNodes: Set<string>;
  
  // Setters
  /** Set nodes */
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  /** Set connections */
  setConnections: (connections: Connection[] | ((prev: Connection[]) => Connection[])) => void;
  /** Set selected nodes */
  setSelectedNodes: (selected: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  
  // Node operations
  /** Add a new node */
  addNode: (node: Omit<Node, 'id'> & { id?: string }) => Node;
  /** Update a node */
  updateNode: (id: string, updates: Partial<Node>) => void;
  /** Delete nodes by IDs */
  deleteNodes: (ids: string[]) => void;
  /** Get a node by ID */
  getNode: (id: string) => Node | undefined;
  /** Find nodes by type */
  getNodesByType: (type: NodeType) => Node[];
  
  // Connection operations
  /** Add a new connection */
  addConnection: (connection: Omit<Connection, 'id'> & { id?: string }) => Connection;
  /** Update a connection */
  updateConnection: (id: string, updates: Partial<Connection>) => void;
  /** Delete connections by IDs */
  deleteConnections: (ids: string[]) => void;
  /** Get a connection by ID */
  getConnection: (id: string) => Connection | undefined;
  /** Get connections for a node */
  getNodeConnections: (nodeId: string) => Connection[];
  
  // Selection operations
  /** Select a node (optionally add to existing selection) */
  selectNode: (id: string, addToSelection?: boolean) => void;
  /** Deselect a node */
  deselectNode: (id: string) => void;
  /** Clear selection */
  clearSelection: () => void;
  /** Select all nodes */
  selectAll: () => void;
  /** Delete selected nodes and their connections */
  deleteSelected: () => void;
  
  // Bulk operations
  /** Clear the entire network */
  clearNetwork: () => void;
  /** Import network state */
  importNetwork: (state: NetworkState) => void;
  /** Export network state */
  exportNetwork: () => NetworkState;
  
  // History
  /** Record current state for undo */
  recordHistory: (description?: string) => void;
  /** Undo last action */
  undo: () => boolean;
  /** Redo last undone action */
  redo: () => boolean;
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  
  // Computed
  /** Network statistics */
  stats: NetworkStats;
  /** Get selected nodes as array */
  selectedNodesList: Node[];
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/** Generate a unique ID */
function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/** Calculate network statistics */
function calculateStats(nodes: Node[], connections: Connection[]): NetworkStats {
  const nodesByType: Record<NodeType, number> = {
    bus: 0,
    generator: 0,
    load: 0,
    storage: 0,
  };

  nodes.forEach((node) => {
    if (node.type in nodesByType) {
      nodesByType[node.type as NodeType]++;
    }
  });

  const lines = connections.filter((c) => c.type === 'line').length;
  const attachments = connections.filter((c) => c.type === 'attachment').length;
  const hasBuses = nodesByType.bus > 0;
  const isValidForPowerFlow = hasBuses && (nodesByType.generator > 0 || nodesByType.load > 0);

  return {
    totalNodes: nodes.length,
    nodesByType,
    totalConnections: connections.length,
    lines,
    attachments,
    hasBuses,
    isValidForPowerFlow,
  };
}

function cloneNode(node: Node): Node {
  switch (node.type) {
    case 'bus':
      return {
        ...node,
        properties: { ...node.properties },
      };
    case 'generator':
      return {
        ...node,
        properties: { ...node.properties },
      };
    case 'load':
      return {
        ...node,
        properties: { ...node.properties },
      };
    case 'storage':
      return {
        ...node,
        properties: { ...node.properties },
      };
    default: {
      const _exhaustive: never = node;
      return _exhaustive;
    }
  }
}

function cloneConnection(connection: Connection): Connection {
  return {
    ...connection,
    properties: { ...connection.properties },
  };
}

// ============================================================
// MAIN HOOK
// ============================================================

/**
 * Centralized state management hook for the network editor
 * 
 * @param options - Configuration options
 * @returns Network state and operations
 * 
 * @example
 * ```tsx
 * const {
 *   nodes,
 *   connections,
 *   selectedNodes,
 *   addNode,
 *   deleteSelected,
 *   undo,
 *   redo,
 *   stats,
 * } = useNetworkState();
 * 
 * // Add a new bus
 * const bus = addNode({
 *   type: 'bus',
 *   x: 100,
 *   y: 100,
 *   name: 'Bus 1',
 *   properties: { v_nom: 380 },
 * });
 * 
 * // Delete selected nodes
 * deleteSelected();
 * 
 * // Undo last action
 * undo();
 * ```
 */
export function useNetworkState(
  options: UseNetworkStateOptions = {}
): UseNetworkStateReturn {
  const {
    initialNodes,
    initialConnections,
    persist = true,
    storageKey = 'network',
  } = options;

  // Core state with optional persistence
   
  const [nodes, setNodesRaw] = persist
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ? useLocalStorage<Node[]>(`${storageKey}_nodes`, initialNodes || [])
    // eslint-disable-next-line react-hooks/rules-of-hooks
    : useState<Node[]>(initialNodes || []);

   
  const [connections, setConnectionsRaw] = persist
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ? useLocalStorage<Connection[]>(`${storageKey}_connections`, initialConnections || [])
    // eslint-disable-next-line react-hooks/rules-of-hooks
    : useState<Connection[]>(initialConnections || []);

  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());

  // Log initialization
  log.info(LOG_CATEGORIES.INIT, 'useNetworkState initialized', {
    initialNodes: nodes.length,
    initialConnections: connections.length,
    persist,
  });

  // History management
  const history = useHistory<NetworkState>();

  // Snapshot current state
  const snapshot = useCallback((): NetworkState => {
    return produce({ nodes, connections }, (draft) => {
      draft.nodes = nodes.map(cloneNode);
      draft.connections = connections.map(cloneConnection);
    });
  }, [nodes, connections]);

  // Wrapped setters that can record history
  const updateNodes = useCallback((recipe: (draft: Node[]) => void | Node[]) => {
    setNodesRaw((prev) => produce(prev, recipe));
  }, [setNodesRaw]);

  const updateConnections = useCallback((recipe: (draft: Connection[]) => void | Connection[]) => {
    setConnectionsRaw((prev) => produce(prev, recipe));
  }, [setConnectionsRaw]);

  const setNodes = useCallback((value: Node[] | ((prev: Node[]) => Node[])) => {
    if (typeof value === 'function') {
      updateNodes((draft) => value(draft));
      return;
    }
    updateNodes(() => value);
  }, [updateNodes]);

  const setConnections = useCallback((value: Connection[] | ((prev: Connection[]) => Connection[])) => {
    if (typeof value === 'function') {
      updateConnections((draft) => value(draft));
      return;
    }
    updateConnections(() => value);
  }, [updateConnections]);

  const updateSelectedNodes = useCallback((recipe: (draft: Set<string>) => void | Set<string>) => {
    setSelectedNodes((prev) => produce(prev, recipe));
  }, [setSelectedNodes]);

  const setSelectedNodesState = useCallback(
    (value: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      if (typeof value === 'function') {
        updateSelectedNodes((draft) => value(draft));
        return;
      }
      updateSelectedNodes(() => value);
    },
    [updateSelectedNodes]
  );

  // Record history (with logging)
  const recordHistory = useCallback((description?: string) => {
    log.info(LOG_CATEGORIES.HISTORY, 'Recording history', { description });
    const before = snapshot();
    // Use setTimeout to capture "after" state
    setTimeout(() => {
      history.push(before, { nodes, connections }, description);
      log.userAction('History Recorded', { description });
    }, 0);
  }, [snapshot, history, nodes, connections]);

  // Undo (with logging)
  const undo = useCallback((): boolean => {
    const state = history.undo();
    const success = !!state;
    if (state) {
      updateNodes(() => state.nodes);
      updateConnections(() => state.connections);
      updateSelectedNodes((draft) => {
        draft.clear();
      });
    }

    try { log.userAction('Undo', { success }); } catch (e) {}
    try { log.info(LOG_CATEGORIES.HISTORY, `Undo performed: ${success}`); } catch (e) {}
    return success;
  }, [history, updateNodes, updateConnections, updateSelectedNodes]);

  // Redo (with logging)
  const redo = useCallback((): boolean => {
    const state = history.redo();
    const success = !!state;
    if (state) {
      updateNodes(() => state.nodes);
      updateConnections(() => state.connections);
      updateSelectedNodes((draft) => {
        draft.clear();
      });
    }

    try { log.userAction('Redo', { success }); } catch (e) {}
    try { log.info(LOG_CATEGORIES.HISTORY, `Redo performed: ${success}`); } catch (e) {}
    return success;
  }, [history, updateNodes, updateConnections, updateSelectedNodes]);

  // ============================================================
  // NODE OPERATIONS
  // ============================================================

  const addNode = useCallback((nodeData: Omit<Node, 'id'> & { id?: string }): Node => {
    let node: Node;
    switch (nodeData.type) {
      case 'bus':
        node = createNode('bus', { x: nodeData.x, y: nodeData.y }, {
          id: nodeData.id,
          name: nodeData.name,
          properties: nodeData.properties,
        });
        break;
      case 'generator':
        node = createNode('generator', { x: nodeData.x, y: nodeData.y }, {
          id: nodeData.id,
          name: nodeData.name,
          properties: nodeData.properties,
        });
        break;
      case 'load':
        node = createNode('load', { x: nodeData.x, y: nodeData.y }, {
          id: nodeData.id,
          name: nodeData.name,
          properties: nodeData.properties,
        });
        break;
      case 'storage':
        node = createNode('storage', { x: nodeData.x, y: nodeData.y }, {
          id: nodeData.id,
          name: nodeData.name,
          properties: nodeData.properties,
        });
        break;
      default: {
        const _exhaustive: never = nodeData.type;
        throw new Error(`Unsupported node type: ${_exhaustive}`);
      }
    }
    
    const before = snapshot();
    updateNodes((draft) => {
      draft.push(node);
    });
    const updatedNodes = produce(nodes, (draft) => {
      draft.push(node);
    });
    history.push(before, { nodes: updatedNodes, connections });

    // Logging
    log.userAction('Node Created', { id: node.id, type: node.type, name: node.name });
    log.networkChange('AddNode', { id: node.id, type: node.type, name: node.name });
    log.info(LOG_CATEGORIES.NODE_OPS, `Node added: ${node.name}`, { id: node.id, type: node.type });
    
    return node;
  }, [snapshot, updateNodes, history, nodes, connections]);

  const updateNode = useCallback(
    (id: string, updates: Partial<Omit<Node, 'type'>> & { properties?: NodeProperties }) => {
    const before = snapshot();
    updateNodes((draft) => {
      const index = draft.findIndex((node) => node.id === id);
      if (index >= 0) {
        draft[index] = { ...draft[index], ...updates } as typeof draft[number];
      }
    });
    const updatedNodes = produce(nodes, (draft) => {
      const index = draft.findIndex((node) => node.id === id);
      if (index >= 0) {
        draft[index] = { ...draft[index], ...updates } as typeof draft[number];
      }
    });
    history.push(before, { nodes: updatedNodes, connections });

    // Logging
    log.info(LOG_CATEGORIES.NODE_OPS, `Node updated: ${id}`, { updates });
    log.userAction('Node Updated', { id, updates });
  }, [snapshot, updateNodes, history, nodes, connections]);

  const deleteNodes = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    const before = snapshot();
    
    const newNodes = produce(nodes, (draft) => draft.filter((node) => !idSet.has(node.id)));
    const newConnections = produce(connections, (draft) =>
      draft.filter((connection) => !idSet.has(connection.from) && !idSet.has(connection.to))
    );
    
    // Perf measurement
    const perfId = `delete-nodes-${Date.now()}`;
    log.startPerf(perfId);

    updateNodes(() => newNodes);
    updateConnections(() => newConnections);
    updateSelectedNodes((draft) => {
      ids.forEach((id) => draft.delete(id));
    });
    
    history.push(before, { nodes: newNodes, connections: newConnections });

    log.endPerf(perfId, LOG_CATEGORIES.NODE_OPS, 'Delete nodes operation', { operationCount: ids.length });
    log.userAction('Nodes Deleted', { count: ids.length, ids });
    log.info(LOG_CATEGORIES.NODE_OPS, `Deleted ${ids.length} nodes`, { ids });
  }, [snapshot, nodes, connections, updateNodes, updateConnections, updateSelectedNodes, history]);

  const getNode = useCallback((id: string): Node | undefined => {
    return nodes.find((n) => n.id === id);
  }, [nodes]);

  const getNodesByType = useCallback((type: NodeType): Node[] => {
    return nodes.filter((n) => n.type === type);
  }, [nodes]);

  // ============================================================
  // CONNECTION OPERATIONS
  // ============================================================

  const addConnection = useCallback((connData: Omit<Connection, 'id'> & { id?: string }): Connection => {
    const connection: Connection = {
      ...connData,
      id: connData.id || generateId('conn'),
    };
    
    const before = snapshot();
    updateConnections((draft) => {
      draft.push(connection);
    });
    const updatedConnections = produce(connections, (draft) => {
      draft.push(connection);
    });
    history.push(before, { nodes, connections: updatedConnections });

    // Logging
    log.userAction('Connection Created', { id: connection.id, from: connection.from, to: connection.to });
    log.networkChange('AddConnection', { id: connection.id, from: connection.from, to: connection.to });
    log.info(LOG_CATEGORIES.CONNECTION_OPS, 'Connection added', { id: connection.id, from: connection.from, to: connection.to });
    
    return connection;
  }, [snapshot, updateConnections, history, nodes, connections]);

  const updateConnection = useCallback((id: string, updates: Partial<Connection>) => {
    const before = snapshot();
    updateConnections((draft) => {
      const index = draft.findIndex((connection) => connection.id === id);
      if (index >= 0) {
        draft[index] = { ...draft[index], ...updates };
      }
    });
    const updatedConnections = produce(connections, (draft) => {
      const index = draft.findIndex((connection) => connection.id === id);
      if (index >= 0) {
        draft[index] = { ...draft[index], ...updates };
      }
    });
    history.push(before, { nodes, connections: updatedConnections });

    // Logging
    log.info(LOG_CATEGORIES.CONNECTION_OPS, `Connection updated: ${id}`, { updates });
    log.userAction('Connection Updated', { id, updates });
  }, [snapshot, updateConnections, history, nodes, connections]);

  const deleteConnections = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    const before = snapshot();

    const perfId = `delete-conns-${Date.now()}`;
    log.startPerf(perfId);
    
    const newConnections = produce(connections, (draft) => draft.filter((connection) => !idSet.has(connection.id)));
    updateConnections(() => newConnections);
    
    history.push(before, { nodes, connections: newConnections });

    log.endPerf(perfId, LOG_CATEGORIES.CONNECTION_OPS, 'Delete connections operation', { operationCount: ids.length });
    log.userAction('Connections Deleted', { count: ids.length, ids });
    log.info(LOG_CATEGORIES.CONNECTION_OPS, `Deleted ${ids.length} connections`, { ids });
  }, [snapshot, connections, updateConnections, history, nodes]);

  const getConnection = useCallback((id: string): Connection | undefined => {
    return connections.find((c) => c.id === id);
  }, [connections]);

  const getNodeConnections = useCallback((nodeId: string): Connection[] => {
    return connections.filter((c) => c.from === nodeId || c.to === nodeId);
  }, [connections]);

  // ============================================================
  // SELECTION OPERATIONS
  // ============================================================

  const selectNode = useCallback((id: string, addToSelection = false) => {
    updateSelectedNodes((draft) => {
      if (addToSelection) {
        log.userAction('Node Selected', { id, addToSelection: true });
        log.debug(LOG_CATEGORIES.SELECTION, `Node selected (add): ${id}`);
        draft.add(id);
        return;
      }
      log.userAction('Node Selected', { id, addToSelection: false });
      log.debug(LOG_CATEGORIES.SELECTION, `Node selected (single): ${id}`);
      draft.clear();
      draft.add(id);
    });
  }, [updateSelectedNodes]);

  const deselectNode = useCallback((id: string) => {
    updateSelectedNodes((draft) => {
      draft.delete(id);
    });
  }, [updateSelectedNodes]);

  const clearSelection = useCallback(() => {
    updateSelectedNodes((draft) => {
      draft.clear();
    });
  }, [updateSelectedNodes]);

  const selectAll = useCallback(() => {
    updateSelectedNodes((draft) => {
      draft.clear();
      nodes.forEach((node) => draft.add(node.id));
    });
  }, [nodes, updateSelectedNodes]);

  const deleteSelected = useCallback(() => {
    // Read the latest selection via functional updater to avoid stale closure
    updateSelectedNodes((draft) => {
      if (draft.size === 0) return;
      const ids = [...draft];
      // Perform deletion using the captured ids
      deleteNodes(ids);
      // Logging
      log.userAction('Nodes Deleted (selected)', { count: ids.length, ids });
      // Ensure selection is cleared immediately
      draft.clear();
    });
  }, [deleteNodes, updateSelectedNodes]);

  // ============================================================
  // BULK OPERATIONS
  // ============================================================

  const clearNetwork = useCallback(() => {
    const before = snapshot();
    log.startPerf('clear-network');
    updateNodes(() => []);
    updateConnections(() => []);
    updateSelectedNodes((draft) => {
      draft.clear();
    });
    history.push(before, { nodes: [], connections: [] });
    log.endPerf('clear-network', LOG_CATEGORIES.NETWORK, 'Clear network operation');
    log.userAction('Network Cleared');
    log.info(LOG_CATEGORIES.NETWORK, 'Network cleared');
  }, [snapshot, updateNodes, updateConnections, updateSelectedNodes, history]);

  const importNetwork = useCallback((state: NetworkState) => {
    const before = snapshot();
    const perfId = `import-${Date.now()}`;
    log.startPerf(perfId);
    updateNodes(() => state.nodes);
    updateConnections(() => state.connections);
    updateSelectedNodes((draft) => {
      draft.clear();
    });
    history.push(before, state);
    log.endPerf(perfId, LOG_CATEGORIES.IMPORT, 'Import network', { operationCount: state.nodes.length });
    log.userAction('Network Imported', { nodes: state.nodes.length, connections: state.connections.length });
    log.info(LOG_CATEGORIES.IMPORT, 'Network imported', { nodes: state.nodes.length, connections: state.connections.length });
  }, [snapshot, updateNodes, updateConnections, updateSelectedNodes, history]);

  const exportNetwork = useCallback((): NetworkState => {
    return produce({ nodes, connections }, (draft) => {
      draft.nodes = nodes.map(cloneNode);
      draft.connections = connections.map(cloneConnection);
    });
  }, [nodes, connections]);




  // ============================================================
  // COMPUTED VALUES
  // ============================================================

  const stats = useMemo(() => calculateStats(nodes, connections), [nodes, connections]);

  const selectedNodesList = useMemo(() => {
    return nodes.filter((n) => selectedNodes.has(n.id));
  }, [nodes, selectedNodes]);

  return {
    // State
    nodes,
    connections,
    selectedNodes,
    
    // Setters
    setNodes,
    setConnections,
    setSelectedNodes: setSelectedNodesState,
    
    // Node operations
    addNode,
    updateNode,
    deleteNodes,
    getNode,
    getNodesByType,
    
    // Connection operations
    addConnection,
    updateConnection,
    deleteConnections,
    getConnection,
    getNodeConnections,
    
    // Selection operations
    selectNode,
    deselectNode,
    clearSelection,
    selectAll,
    deleteSelected,
    
    // Bulk operations
    clearNetwork,
    importNetwork,
    exportNetwork,
    
    // History
    recordHistory,
    undo,
    redo,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    
    // Computed
    stats,
    selectedNodesList,
  };
}

export default useNetworkState;
